// Time-preserving granular pitch shift (overlap-add, Hann window)
import { SCALE_STEPS, KEYS } from "@/lib/music";

export function pitchShift(ctx, buffer, semitones) {
  return semitones ? granular(ctx, buffer, () => semitones) : buffer;
}

// semisAt(sampleIndex) gives the shift for the grain centred at that sample
function granular(ctx, buffer, semisAt) {
  const N = 2048;
  const H = N / 4;
  const win = new Float32Array(N);
  for (let i = 0; i < N; i++) win[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (N - 1));
  const len = buffer.length;
  const out = ctx.createBuffer(buffer.numberOfChannels, len, buffer.sampleRate);
  const norm = new Float32Array(len);
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const x = buffer.getChannelData(ch);
    const y = out.getChannelData(ch);
    for (let o = -N; o < len; o += H) {
      const ratio = Math.pow(2, semisAt(Math.max(0, o + N / 2)) / 12);
      const start = o + N / 2 - (N * ratio) / 2;
      for (let i = 0; i < N; i++) {
        const oi = o + i;
        if (oi < 0 || oi >= len) continue;
        const p = start + i * ratio;
        const k = Math.floor(p);
        if (k < 0 || k + 1 >= len) continue;
        const f = p - k;
        y[oi] += (x[k] * (1 - f) + x[k + 1] * f) * win[i];
        if (ch === 0) norm[oi] += win[i];
      }
    }
  }
  for (let ch = 0; ch < out.numberOfChannels; ch++) {
    const y = out.getChannelData(ch);
    for (let i = 0; i < len; i++) y[i] = norm[i] > 0.05 ? y[i] / norm[i] : 0;
  }
  return out;
}

// YIN-style pitch tracker on a 4x downsampled signal; returns MIDI per frame (NaN = unvoiced)
export function trackPitch(buffer) {
  const D = 4;
  const x = buffer.getChannelData(0);
  const sr = buffer.sampleRate / D;
  const ds = new Float32Array(Math.floor(x.length / D));
  for (let i = 0; i < ds.length; i++) ds[i] = (x[i * D] + x[i * D + 1] + x[i * D + 2] + x[i * D + 3]) / 4;
  const W = 384;
  const hop = 256;
  const minLag = Math.floor(sr / 1000);
  const maxLag = Math.floor(sr / 70);
  const d = new Float32Array(maxLag + 1);
  const frames = Math.max(1, Math.ceil(ds.length / hop));
  const midi = new Float32Array(frames).fill(NaN);
  for (let f = 0; f < frames; f++) {
    const s = f * hop;
    if (s + W + maxLag >= ds.length) break;
    let e = 0;
    for (let i = 0; i < W; i++) e += ds[s + i] * ds[s + i];
    if (Math.sqrt(e / W) < 0.01) continue;
    let run = 0;
    let best = -1;
    for (let tau = 1; tau <= maxLag; tau++) {
      let sum = 0;
      for (let i = 0; i < W; i++) {
        const diff = ds[s + i] - ds[s + i + tau];
        sum += diff * diff;
      }
      run += sum;
      d[tau] = (sum * tau) / (run || 1);
    }
    for (let tau = minLag; tau < maxLag; tau++) {
      if (d[tau] < 0.15 && d[tau] <= d[tau + 1]) {
        best = tau;
        break;
      }
    }
    if (best > 0) midi[f] = 69 + 12 * Math.log2(sr / best / 440);
  }
  // 3-point median to remove octave blips
  const sm = Float32Array.from(midi);
  for (let f = 1; f < frames - 1; f++) {
    const v = [midi[f - 1], midi[f], midi[f + 1]];
    if (v.some(Number.isNaN)) continue;
    sm[f] = v.sort((a, b) => a - b)[1];
  }
  // bridge short unvoiced gaps (glides, consonant blips) so they get shifted too
  for (let f = 1; f < frames; f++) {
    if (!Number.isNaN(sm[f]) || Number.isNaN(sm[f - 1])) continue;
    let g = f;
    while (g < frames && Number.isNaN(sm[g])) g++;
    if (g < frames && g - f <= 5) for (let k = f; k < g; k++) sm[k] = sm[f - 1] + ((sm[g] - sm[f - 1]) * (k - f + 1)) / (g - f + 1);
    f = g;
  }
  return { midi: sm, hopSamples: hop * D };
}

function diatonicInterval(m, keyPc, scale, steps) {
  let n = Math.round(m);
  let bestN = n;
  let bestD = Infinity;
  for (let c = n - 2; c <= n + 2; c++) {
    if (scale.includes((((c - keyPc) % 12) + 12) % 12) && Math.abs(c - m) < bestD) {
      bestD = Math.abs(c - m);
      bestN = c;
    }
  }
  const rel = bestN - keyPc;
  const oct = Math.floor(rel / 12);
  const idx = scale.indexOf(((rel % 12) + 12) % 12) + steps;
  const target = keyPc + 12 * (oct + Math.floor(idx / 7)) + scale[((idx % 7) + 7) % 7];
  return target - bestN;
}

// In-key harmony: returns semitone shift per sample, following the scale note-by-note
export function diatonicSemis({ midi, hopSamples }, key, scaleName, steps) {
  const scale = SCALE_STEPS[scaleName] || SCALE_STEPS.Minor;
  const keyPc = Math.max(0, KEYS.indexOf(key));
  const fallback = Math.sign(steps) * [0, 2, 4, 5, 7, 9, 11, 12][Math.abs(steps)];
  const raw = Array.from(midi, (m) => (Number.isNaN(m) ? null : diatonicInterval(m, keyPc, scale, steps)));
  let last = raw.find((v) => v !== null) ?? fallback;
  const held = raw.map((v) => (v === null ? last : (last = v)));
  const semis = held.map((v, i) => {
    const a = held[i - 1] ?? v;
    const b = held[i + 1] ?? v;
    return a === b ? a : v;
  });
  return (s) => semis[Math.min(semis.length - 1, Math.floor(s / hopSamples))];
}

const mulberry = (seed) => () => {
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

export const seedOf = (str) => [...str].reduce((h, c) => (Math.imul(h, 31) + c.charCodeAt(0)) | 0, 7);

// TD-PSOLA: shifts pitch while keeping formants (no chipmunk), plus subtle human timing/pitch drift
export function psolaShift(ctx, buffer, { midi, hopSamples }, semisAt, seed = 1) {
  const sr = buffer.sampleRate;
  const len = buffer.length;
  const f0At = (p) => {
    const m = midi[Math.min(midi.length - 1, Math.floor(p / hopSamples))];
    return Number.isNaN(m) ? 0 : 440 * Math.pow(2, (m - 69) / 12);
  };
  const uT = Math.round(sr * 0.005);
  const marks = [];
  for (let p = 0; p < len; ) {
    const f = f0At(p);
    const T = f > 0 ? sr / f : uT;
    marks.push([Math.round(p), T, f > 0]);
    p += T;
  }
  const rnd = mulberry(seed);
  const delay = Math.round(sr * (0.008 + rnd() * 0.018));
  const phase = rnd() * Math.PI * 2;
  const driftHz = 0.15 + rnd() * 0.2;
  const chans = buffer.numberOfChannels;
  const out = ctx.createBuffer(chans, len, sr);
  const xs = [...Array(chans)].map((_, c) => buffer.getChannelData(c));
  const ys = [...Array(chans)].map((_, c) => out.getChannelData(c));
  const norm = new Float32Array(len);
  let mi = 0;
  for (let ts = delay; ts < len; ) {
    const ta = ts - delay;
    while (mi + 1 < marks.length && Math.abs(marks[mi + 1][0] - ta) <= Math.abs(marks[mi][0] - ta)) mi++;
    const [c, T0, voiced] = marks[mi];
    const semis = voiced ? semisAt(c) + 0.07 * Math.sin((2 * Math.PI * driftHz * ts) / sr + phase) : 0;
    const Ts = voiced ? T0 / Math.pow(2, semis / 12) : T0;
    const half = Math.max(8, Math.round(Math.min(Math.max(T0, Ts), 4 * T0)));
    const base = Math.round(ts);
    for (let i = -half; i <= half; i++) {
      const a = c + i;
      const b = base + i;
      if (a < 0 || a >= len || b < 0 || b >= len) continue;
      const w = 0.5 + 0.5 * Math.cos((Math.PI * i) / half);
      for (let ch = 0; ch < chans; ch++) ys[ch][b] += xs[ch][a] * w;
      norm[b] += w;
    }
    ts += Ts;
  }
  for (let ch = 0; ch < chans; ch++) {
    const y = ys[ch];
    for (let i = 0; i < len; i++) if (norm[i] > 0.05) y[i] /= Math.max(norm[i], 0.6);
  }
  matchLoudness(xs[0], ys, delay);
  return out;
}

// Follow the source's dynamics (block RMS, smoothed) so shifted voices keep a natural level
function matchLoudness(x, ys, delay) {
  const B = 1024;
  const len = x.length;
  const n = Math.ceil(len / B);
  const gain = new Float32Array(n);
  for (let k = 0; k < n; k++) {
    let ex = 0;
    let ey = 0;
    for (let i = k * B; i < Math.min(len, (k + 1) * B); i++) {
      const xi = x[i - delay] || 0;
      ex += xi * xi;
      ey += ys[0][i] * ys[0][i];
    }
    gain[k] = ey > 1e-9 ? Math.min(2.5, Math.max(0.4, Math.sqrt(ex / ey))) : 1;
  }
  const sm = gain.map((g, k) => ((gain[k - 1] ?? g) + 2 * g + (gain[k + 1] ?? g)) / 4);
  for (const y of ys) {
    for (let i = 0; i < len; i++) {
      const pos = i / B - 0.5;
      const k = Math.max(0, Math.min(n - 1, Math.floor(pos)));
      const f = Math.max(0, Math.min(1, pos - k));
      y[i] *= sm[k] * (1 - f) + (sm[Math.min(n - 1, k + 1)] ?? sm[k]) * f;
    }
  }
}

export function sliceBuffer(ctx, buffer, startSec, lenSec) {
  const a = Math.max(0, Math.floor(startSec * buffer.sampleRate));
  const b = Math.min(buffer.length, a + Math.floor(lenSec * buffer.sampleRate));
  const out = ctx.createBuffer(buffer.numberOfChannels, Math.max(1, b - a), buffer.sampleRate);
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) out.copyToChannel(buffer.getChannelData(ch).subarray(a, b), ch);
  return out;
}

export function trimStart(ctx, buffer, seconds) {
  const skip = Math.min(buffer.length - 1, Math.floor(seconds * buffer.sampleRate));
  if (skip <= 0) return buffer;
  const out = ctx.createBuffer(buffer.numberOfChannels, buffer.length - skip, buffer.sampleRate);
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) out.copyToChannel(buffer.getChannelData(ch).subarray(skip), ch);
  return out;
}

export function makeImpulse(ctx, seconds) {
  const sr = ctx.sampleRate;
  const len = Math.max(1, Math.floor(sr * seconds));
  const ir = ctx.createBuffer(2, len, sr);
  for (let ch = 0; ch < 2; ch++) {
    const d = ir.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3.2);
  }
  return ir;
}

export function computePeaks(buffer, n) {
  const data = buffer.getChannelData(0);
  const step = Math.max(1, Math.floor(data.length / n));
  const stride = Math.max(1, Math.floor(step / 32));
  const peaks = new Float32Array(n);
  for (let b = 0; b < n; b++) {
    let m = 0;
    const s = b * step;
    for (let i = s; i < s + step && i < data.length; i += stride) {
      const v = Math.abs(data[i]);
      if (v > m) m = v;
    }
    peaks[b] = m;
  }
  return peaks;
}

export function encodeWav(buffer) {
  const ch = buffer.numberOfChannels;
  const sr = buffer.sampleRate;
  const len = buffer.length;
  const ab = new ArrayBuffer(44 + len * ch * 2);
  const v = new DataView(ab);
  const str = (o, s) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  str(0, "RIFF");
  v.setUint32(4, 36 + len * ch * 2, true);
  str(8, "WAVE");
  str(12, "fmt ");
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, ch, true);
  v.setUint32(24, sr, true);
  v.setUint32(28, sr * ch * 2, true);
  v.setUint16(32, ch * 2, true);
  v.setUint16(34, 16, true);
  str(36, "data");
  v.setUint32(40, len * ch * 2, true);
  const chans = [...Array(ch)].map((_, i) => buffer.getChannelData(i));
  let o = 44;
  for (let i = 0; i < len; i++) {
    for (let c = 0; c < ch; c++) {
      const s = Math.max(-1, Math.min(1, chans[c][i]));
      v.setInt16(o, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      o += 2;
    }
  }
  return new Blob([ab], { type: "audio/wav" });
}

// Procedural "ah" chant in D minor so users can try harmonies instantly
export async function synthDemoVocal() {
  const sr = 44100;
  const beat = 0.75;
  const notes = [[62, 1], [65, 1], [64, 0.5], [62, 0.5], [69, 1.5], [67, 0.5], [65, 1], [64, 1], [62, 2.5]];
  const total = notes.reduce((a, n) => a + n[1], 0) * beat + 1.2;
  const off = new OfflineAudioContext(1, Math.ceil(sr * total), sr);
  const osc = off.createOscillator();
  osc.type = "sawtooth";
  const vib = off.createOscillator();
  vib.frequency.value = 5.3;
  const vibG = off.createGain();
  vibG.gain.value = 3.5;
  vib.connect(vibG).connect(osc.frequency);
  const amp = off.createGain();
  amp.gain.value = 0;
  osc.connect(amp);
  const out = off.createGain();
  out.connect(off.destination);
  [[730, 1, 10], [1090, 0.5, 12], [2440, 0.25, 14]].forEach(([f, g, q]) => {
    const bp = off.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = f;
    bp.Q.value = q;
    const gn = off.createGain();
    gn.gain.value = g * 3;
    amp.connect(bp).connect(gn).connect(out);
  });
  const body = off.createBiquadFilter();
  body.type = "lowpass";
  body.frequency.value = 500;
  const bodyG = off.createGain();
  bodyG.gain.value = 0.35;
  amp.connect(body).connect(bodyG).connect(out);
  let t = 0.3;
  osc.frequency.setValueAtTime(440 * Math.pow(2, (notes[0][0] - 69) / 12), 0);
  notes.forEach(([m, b], idx) => {
    const d = b * beat;
    osc.frequency.setTargetAtTime(440 * Math.pow(2, (m - 69) / 12), t, 0.025);
    amp.gain.setTargetAtTime(0.9, t, 0.06);
    amp.gain.setTargetAtTime(idx === notes.length - 1 ? 0 : 0.55, t + d - 0.1, idx === notes.length - 1 ? 0.25 : 0.04);
    t += d;
  });
  osc.start(0);
  vib.start(0);
  const buf = await off.startRendering();
  const d = buf.getChannelData(0);
  let peak = 0;
  for (let i = 0; i < d.length; i++) peak = Math.max(peak, Math.abs(d[i]));
  if (peak > 0) for (let i = 0; i < d.length; i++) d[i] = (d[i] / peak) * 0.8;
  return buf;
}
